// Event settings: edit core event row + listing of ticket_types.

import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCurrentEvent } from "@/lib/event-context";
import { listTicketTypes } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { fmtEuro } from "@/lib/formatters";

const EVENT_TIME_ZONE = "Europe/Amsterdam";

function timeZoneOffsetMs(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const asUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second),
  );
  return asUtc - date.getTime();
}

function formatAmsterdamDateTimeLocal(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: EVENT_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}`;
}

function amsterdamDateTimeLocalToIso(value: string): string {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return new Date(value).toISOString();

  const [, year, month, day, hour, minute] = match;
  const utcGuess = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    0,
  );
  const guessedDate = new Date(utcGuess);
  const offset = timeZoneOffsetMs(guessedDate, EVENT_TIME_ZONE);
  let utcTime = utcGuess - offset;

  const correctedOffset = timeZoneOffsetMs(new Date(utcTime), EVENT_TIME_ZONE);
  if (correctedOffset !== offset) {
    utcTime = utcGuess - correctedOffset;
  }

  return new Date(utcTime).toISOString();
}

export function CmsEvent() {
  const { data: event } = useCurrentEvent();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [venue, setVenue] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [capacity, setCapacity] = useState(600);
  const [published, setPublished] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (event) {
      setName(event.name);
      setVenue(event.venue ?? "");
      setStartsAt(formatAmsterdamDateTimeLocal(event.starts_at));
      setCapacity(event.capacity);
      setPublished(event.published);
    }
  }, [event]);

  const { data: types } = useQuery({
    queryKey: ["ticket_types", event?.id],
    queryFn: () => listTicketTypes(event!.id),
    enabled: !!event?.id,
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("events")
        .update({
          name,
          venue,
          starts_at: amsterdamDateTimeLocalToIso(startsAt),
          capacity,
          published,
        })
        .eq("id", event!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      setToast("✓ Event opgeslagen");
      qc.invalidateQueries({ queryKey: ["events"] });
      setTimeout(() => setToast(null), 2000);
    },
  });

  if (!event) return <div className="empty"><span className="spinner" /> Laden…</div>;

  return (
    <>
      <div className="row2">
        <div className="panel">
          <div className="panel-h"><h3>Event</h3></div>

          <div className="field"><label>Naam</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field"><label>Locatie</label>
            <input value={venue} onChange={(e) => setVenue(e.target.value)} />
          </div>
          <div className="form-grid">
            <div className="field"><label>Start</label>
              <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </div>
            <div className="field"><label>Capaciteit</label>
              <input type="number" value={capacity} onChange={(e) => setCapacity(Number(e.target.value))} />
            </div>
          </div>
          <div className="field">
            <label style={{ display: "inline-flex", gap: "0.5rem", alignItems: "center" }}>
              <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
              Gepubliceerd (zichtbaar voor publiek)
            </label>
          </div>

          <button className="btn-admin sm" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            💾 Opslaan
          </button>
        </div>

        <div className="panel">
          <div className="panel-h"><h3>Ticket types</h3></div>
          {(types ?? []).map((t) => (
            <div key={t.id} style={{ padding: "0.7rem 0", borderBottom: "1px dashed rgba(255,122,26,0.2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <strong>{t.name}</strong>
                <span style={{ color: "var(--c-orange)", fontFamily: "var(--f-display)" }}>{fmtEuro(t.price_cents)}</span>
              </div>
              <small>{t.description}</small><br />
              <small>code: <code>{t.code}</code> · flow: {t.flow} · BTW {(t.vat_rate * 100).toFixed(0)}% · incl. {t.includes_tickets} ticket(s)</small>
            </div>
          ))}
          <p style={{ marginTop: "1rem", fontSize: "0.78rem", opacity: 0.6 }}>
            Inline editor voor ticket-types komt in v1.1. Pas waarden voor nu aan via Supabase studio of een SQL migration.
          </p>
        </div>
      </div>

      {toast && <div className="toast success">{toast}</div>}
    </>
  );
}
